'use client';

import { useEffect, useRef } from 'react';

type LenisLike = { scrollTo: (target: number, opts?: { immediate?: boolean }) => void };

/**
 * Thin overlay scrollbar — the native scrollbar is hidden (globals.css) so it no
 * longer reserves layout width; this floats over the content on the trailing
 * edge (RTL-aware) and tracks window scroll (driven by Lenis). The thumb is
 * grab-and-drag, the track is click-to-jump, and it dims to a quiet resting
 * state a moment after you stop. Desktop only; touch keeps native scrollbars.
 */
export function CustomScrollbar() {
  const wrapRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let idle: ReturnType<typeof setTimeout>;
    let dragging = false;
    let startY = 0;
    let startScroll = 0;

    const metrics = () => {
      const winH = window.innerHeight;
      const scrollH = document.documentElement.scrollHeight;
      const maxScroll = Math.max(scrollH - winH, 0);
      const trackH = wrapRef.current?.clientHeight ?? 0;
      const thumbH = Math.max((winH / scrollH) * trackH, 44);
      return { winH, scrollH, maxScroll, trackH, thumbH };
    };

    const scrollTo = (y: number) => {
      const { maxScroll } = metrics();
      const clamped = Math.min(Math.max(y, 0), maxScroll);
      const lenis = (window as unknown as { lenis?: LenisLike }).lenis;
      if (lenis) lenis.scrollTo(clamped, { immediate: true });
      else window.scrollTo(0, clamped);
    };

    const paint = () => {
      const wrap = wrapRef.current;
      const thumb = thumbRef.current;
      if (!wrap || !thumb) return;
      const { maxScroll, trackH, thumbH } = metrics();
      if (maxScroll <= 8) {
        wrap.style.opacity = '0';
        return;
      }
      const top = (window.scrollY / maxScroll) * (trackH - thumbH);
      thumb.style.height = `${thumbH}px`;
      thumb.style.transform = `translateY(${top}px)`;
      wrap.style.opacity = '1';
      clearTimeout(idle);
      if (!dragging) {
        idle = setTimeout(() => {
          if (wrapRef.current) wrapRef.current.style.opacity = '0.35';
        }, 900);
      }
    };

    const onThumbDown = (e: PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragging = true;
      startY = e.clientY;
      startScroll = window.scrollY;
      document.body.style.userSelect = 'none';
      thumbRef.current?.classList.add('cursor-grabbing');
    };

    const onTrackDown = (e: PointerEvent) => {
      const { trackH, thumbH, maxScroll } = metrics();
      const rect = wrapRef.current!.getBoundingClientRect();
      const y = ((e.clientY - rect.top - thumbH / 2) / (trackH - thumbH)) * maxScroll;
      scrollTo(y);
    };

    const onMove = (e: PointerEvent) => {
      if (!dragging) return;
      const { trackH, thumbH, maxScroll } = metrics();
      const range = trackH - thumbH;
      if (range <= 0) return;
      scrollTo(startScroll + ((e.clientY - startY) / range) * maxScroll);
      paint();
    };

    const onUp = () => {
      if (!dragging) return;
      dragging = false;
      document.body.style.userSelect = '';
      thumbRef.current?.classList.remove('cursor-grabbing');
      paint();
    };

    paint();
    window.addEventListener('scroll', paint, { passive: true });
    window.addEventListener('resize', paint);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    const thumb = thumbRef.current;
    const wrap = wrapRef.current;
    thumb?.addEventListener('pointerdown', onThumbDown);
    wrap?.addEventListener('pointerdown', onTrackDown);
    return () => {
      window.removeEventListener('scroll', paint);
      window.removeEventListener('resize', paint);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      thumb?.removeEventListener('pointerdown', onThumbDown);
      wrap?.removeEventListener('pointerdown', onTrackDown);
      clearTimeout(idle);
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      aria-hidden
      className="fixed end-2 top-3 bottom-3 z-[90] w-3.5 cursor-pointer opacity-0 transition-opacity duration-300 max-[850px]:hidden"
    >
      <div
        ref={thumbRef}
        style={{ touchAction: 'none' }}
        className="mx-auto w-2 cursor-grab rounded-full bg-primary/55 transition-[width,background-color] duration-150 hover:w-2.5 hover:bg-primary/75"
      />
    </div>
  );
}
